import type { ActiveFilterRemoval } from '@hyphengroup/cms-components/cms-blocks/SortAndFilterPanel/ActiveFilterChips';
import type {
  FilterChipActivation,
  FilterToggle,
} from '@hyphengroup/cms-components/cms-blocks/SortAndFilterPanel/FilterGroups';
import { track } from 'utils/tracking';
import type { VerticalType } from '../../types';
import type { ComponentAction, ComponentType } from '$/types/tracking';

/**
 * The Sort & Filter panel's GA4 events (CMS-1915, AirTable #2525-#2533; on
 * credit card CMS-1902, #2488 and #2730). Every control fires the same
 * `filter_action`; only the parameters differ.
 */

const EVENT = 'filter_action';
const RESET_FILTER_KEY = 'reset';

/** What every event needs and no control decides for itself. */
export type PanelTrackingContext = {
  /** Block position of the panel on the page, as `component_index`. */
  componentIndex: string;
  /**
   * The page's own vertical, from the layout rather than from the sort config:
   * the plan's reporting names are travel insurance's, and the config is a
   * cms-components contract that has no reason to carry a vertical.
   */
  vertical?: VerticalType;
};

type FilterAction = {
  componentType: ComponentType;
  componentTitle: string;
  componentIndex: string;
  action: ComponentAction;
  filterKey: string;
  filterValue: string;
};

function trackFilterAction({
  componentType,
  componentTitle,
  componentIndex,
  action,
  filterKey,
  filterValue,
}: FilterAction): void {
  track({
    event: EVENT,
    component_type: componentType,
    component_title: componentTitle,
    component_index: componentIndex,
    action,
    filter_key: filterKey,
    filter_value: filterValue,
  });
}

/** The group as the plan reports it, which is not what the panel displays. */
type ReportingGroup = { title: string; key: string };

type ReportingGroups = Readonly<Record<string, ReportingGroup>>;

/**
 * The four TI checkbox groups' reporting names (#2526-#2529). Neither string is
 * derivable from the panel: the heading reads "CHECKOUT" where the plan wants
 * "Checkout Option", and #2527 pairs the plural title "Providers" with the
 * singular key "Provider". Matched on the editor's `category_id` first and the
 * group heading second, since either is the identifier an editor is likely to
 * have authored.
 *
 * `offers` is the `category_id` the Rewards group carries in both markets; a
 * zh-HK heading normalises to nothing, so on MoneyHero the id is the only match.
 */
const TI_CHECKBOX_GROUPS: ReportingGroups = {
  checkout: { title: 'Checkout Option', key: 'Checkout Option' },
  checkoutoption: { title: 'Checkout Option', key: 'Checkout Option' },
  provider: { title: 'Providers', key: 'Provider' },
  coverage: { title: 'Coverage', key: 'Coverage' },
  reward: { title: 'Rewards', key: 'Rewards' },
  offer: { title: 'Rewards', key: 'Rewards' },
};

/**
 * The chips group's reporting name (#2530). Separate from the checkbox table: a
 * checkbox group whose `category_id` reads `special` is not the Special chips.
 */
const TI_CHIPS_GROUPS: ReportingGroups = {
  special: { title: 'Special', key: 'Special' },
  lookingforsomethingspecial: { title: 'Special', key: 'Special' },
};

/**
 * Where an unmatched group is worth saying out loud. Staging runs the
 * production build (`Dockerfile:4`, `:40`), so `NODE_ENV` alone would hide this
 * from the one environment AC 8 is actually verified on.
 */
const isVerifiableEnvironment = (): boolean =>
  process.env.NODE_ENV !== 'production' ||
  (typeof window !== 'undefined' &&
    window.location.hostname.endsWith('mhg-staging.com'));

const normalise = (value: string | undefined): string =>
  (value ?? '')
    .toLowerCase()
    .replace(/[^a-z]/g, '')
    .replace(/s$/, '');

type FilterGroupRef = Pick<FilterChipActivation, 'categoryId' | 'categoryName'>;

/**
 * Gated on the vertical because the names above are generic words: a personal
 * loan page's Provider group would otherwise be renamed by a plan written for
 * travel insurance. Credit card keys by the group's `category_id`, which reads
 * the same in both MoneyHero locales where the heading does not (CMS-1902,
 * #2488, #2730). Every other vertical reports the group's own heading as both
 * title and key, which is what it did before CMS-1915.
 */
const resolveGroup = (
  { categoryId, categoryName }: FilterGroupRef,
  vertical: VerticalType | undefined,
  groups: ReportingGroups,
): ReportingGroup => {
  const mapped =
    vertical === 'TRAVEL_INSURANCE'
      ? (groups[normalise(categoryId)] ?? groups[normalise(categoryName)])
      : undefined;
  if (mapped) return mapped;

  // Both inputs are editor free text that changes without a deploy (CMS-1893
  // R2.2, R11), and a chips group has no `category_id` at all (R2.3), so a
  // rename is enough to send a key reporting does not recognise. Nothing else
  // would surface that until someone reads GA4 weeks later.
  if (vertical === 'TRAVEL_INSURANCE' && isVerifiableEnvironment()) {
    console.warn(
      `[CMS-1915] Filter group "${categoryName ?? categoryId}" matched no ` +
        'reporting name; falling back to its heading. Check `category_id` and ' +
        'the heading against the AirTable plan (#2526-#2530).',
    );
  }

  // A chips group carries no `category_id` at all, so the heading can be the
  // only identifier there is.
  const fallback = categoryName?.trim() || categoryId || '';
  return {
    title: fallback,
    key: (vertical === 'CREDIT_CARD' && categoryId) || fallback,
  };
};

/** #2525. The title carries the option the reader clicked, as shown to them. */
export function trackSortSelect(
  { componentIndex }: PanelTrackingContext,
  label: string,
): void {
  trackFilterAction({
    componentType: 'sidebar',
    componentTitle: `Sort/Dropdown_${label}`,
    componentIndex,
    action: 'select',
    filterKey: 'sort',
    filterValue: label,
  });
}

/** #2526-#2529. `action` names the state the click produced, never both. */
export function trackFilterToggle(
  { componentIndex, vertical }: PanelTrackingContext,
  toggle: FilterToggle,
): void {
  const group = resolveGroup(toggle, vertical, TI_CHECKBOX_GROUPS);
  trackFilterAction({
    componentType: 'sidebar',
    componentTitle: `Filter Attribute/Checkbox_${group.title}`,
    componentIndex,
    action: toggle.checked ? 'check' : 'uncheck',
    filterKey: group.key,
    filterValue: toggle.label,
  });
}

/**
 * #2530. A chips group navigates to a category page rather than toggling
 * (CMS-1542 R4.5), so `select` is the only resolvable action — there is no
 * state to deselect, and the chip for the current page is inert.
 */
export function trackFilterChipSelect(
  { componentIndex, vertical }: PanelTrackingContext,
  chip: FilterChipActivation,
): void {
  const group = resolveGroup(chip, vertical, TI_CHIPS_GROUPS);
  trackFilterAction({
    componentType: 'sidebar',
    componentTitle: `Filter Attribute/Chips_${group.title}`,
    componentIndex,
    action: 'select',
    filterKey: group.key,
    filterValue: chip.label,
  });
}

/** #2531. `chip`, not `sidebar`: this row sits above the results. */
export function trackActiveFilterRemove(
  { componentIndex, vertical }: PanelTrackingContext,
  removal: ActiveFilterRemoval,
): void {
  trackFilterAction({
    componentType: 'chip',
    componentTitle: 'Filter Attribute/Chips_Remove',
    componentIndex,
    action: 'click',
    // Only checkbox groups hold a selection, so a removed chip belongs to one.
    filterKey: resolveGroup(removal, vertical, TI_CHECKBOX_GROUPS).key,
    filterValue: removal.label,
  });
}

/**
 * #2532 (header text link) and #2533 (footer button). Two controls doing the
 * same thing, kept apart by their titles because reporting counts them apart.
 */
export function trackClearAll(
  { componentIndex }: PanelTrackingContext,
  source: 'header' | 'footer',
): void {
  trackFilterAction({
    componentType: 'sidebar',
    componentTitle:
      source === 'header'
        ? 'Filter Attribute/text-link_Clear All'
        : 'Filter Attribute/Button_Clear All',
    componentIndex,
    action: 'click',
    filterKey: RESET_FILTER_KEY,
    filterValue: '',
  });
}

/**
 * The "Your details" group (CMS-1905), tracked as the eligibility filter:
 * #2732 (citizenship) and #2733 (monthly income), specified under CMS-2063.
 */

const ELIGIBILITY_FILTER_KEYS = {
  citizenship: 'Citizenship',
  income: 'Monthly Income',
  panel: 'Sort and Filter',
} as const;

/**
 * Income is reported as a band, never the figure the reader typed (Analytics
 * 2.1). The boundaries are round numbers in the market's own currency rather
 * than a conversion: every market's readers are compared against their own
 * scale, which is what a placement question needs.
 */
const INCOME_BANDS: readonly { max: number; label: string }[] = [
  { max: 2_000, label: '0-2000' },
  { max: 4_000, label: '2000-4000' },
  { max: 6_000, label: '4000-6000' },
  { max: 10_000, label: '6000-10000' },
  { max: 20_000, label: '10000-20000' },
];

export const incomeBand = (value: number): string =>
  INCOME_BANDS.find((band) => value < band.max)?.label ?? '20000+';

/** #2732 — which option the reader chose. */
export function trackCitizenshipSelect(
  { componentIndex }: PanelTrackingContext,
  value: string,
): void {
  trackFilterAction({
    componentType: 'sidebar',
    componentTitle: `Eligibility Filter/Dropdown_${ELIGIBILITY_FILTER_KEYS.citizenship}`,
    componentIndex,
    action: 'select',
    filterKey: ELIGIBILITY_FILTER_KEYS.citizenship,
    filterValue: value,
  });
}

/**
 * #2733 — fired once the reader has finished typing, and carrying the
 * band rather than the figure. Clearing the field reports nothing: there is no
 * band for "no income", and an empty value would read as a zero-income reader.
 */
export function trackIncomeEntered(
  { componentIndex }: PanelTrackingContext,
  value: number | undefined,
): void {
  if (!value) return;
  trackFilterAction({
    componentType: 'sidebar',
    componentTitle: `Eligibility Filter/Textbox_${ELIGIBILITY_FILTER_KEYS.income}`,
    componentIndex,
    // A textbox the reader fills, not a dropdown they select from.
    action: 'fill',
    filterKey: ELIGIBILITY_FILTER_KEYS.income,
    filterValue: incomeBand(value),
  });
}

/**
 * #2748 (CMS-1905 Analytics 4) — what separates "never opened the panel" from
 * "opened it and did not fill it in". On mobile the two inputs sit behind this button, so
 * without it a flat result cannot be read as placement rather than feature.
 */
export function trackPanelOpen({ componentIndex }: PanelTrackingContext): void {
  trackFilterAction({
    // The trigger button, not the panel it opens.
    componentType: 'button',
    componentTitle: `Filter Attribute/Button_${ELIGIBILITY_FILTER_KEYS.panel}`,
    componentIndex,
    action: 'click',
    filterKey: 'open',
    filterValue: '',
  });
}
